import React from 'react';
import {
  ChartBarIcon,
  UserIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  EyeIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

const TargetIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
  </svg>
);

const iconMap = {
  ChartBarIcon,
  UserIcon,
  SearchIcon: MagnifyingGlassIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ClipboardDocumentIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  EyeIcon,
  TargetIcon,
};

const GettingStartedMiniCard = ({ activity, completed = false, locked = false, lockedLabel = 'Pending Approval', badgeLabel = null }) => {
  const IconComponent = iconMap[activity.icon] || DocumentTextIcon;

  const handleActivate = (e) => {
    if (locked) { e?.preventDefault?.(); return; }
    
    // Track clicks for analytics
    try { 
      if (window.ahoy?.track) {
        window.ahoy.track(`getting_started_${activity.id}_clicked`);
      }
    } catch(_) {}
    
    // Render hint: an activity may intercept its own activation instead of
    // navigating — e.g. the RJ marketplace visibility card opens an inline
    // sheet in place. Mirrors the `videoTask` hint QuickstartBlock uses.
    if (typeof activity.onActivate === 'function') {
      e?.preventDefault?.();
      activity.onActivate();
      return;
    }

    // Special handling for succession plan card - always go directly to the plan
    if (activity.id === 'find_my_gaps') {
      e?.preventDefault?.();
      window.location.href = '/succession/plans/comprehensive-succession-plan-v2';
      return;
    }
    
    const path = activity.path;
    if (!path) return;
    if (activity.id === 'consultation') {
      try { window.open(path, '_blank', 'noopener'); } catch(_) { window.location.href = path; }
    } else {
      window.location.href = path;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate(e);
    }
  };

  // Card styling based on completion/locked state
  const cardClasses = locked
    ? 'bg-succession-bg-tertiary border-succession-border opacity-60 cursor-not-allowed'
    : completed
      ? 'bg-succession-success-bg border-succession-success'
      : 'bg-succession-bg-tertiary border-succession-border hover:border-succession-success hover:bg-succession-hover-bg cursor-pointer';

  // Icon container styling based on completion state
  const iconContainerClasses = locked
    ? 'bg-succession-bg-secondary'
    : completed
      ? 'bg-succession-success-bg'
      : activity.bgColor;

  const iconClasses = locked
    ? 'text-succession-text-muted'
    : completed
      ? 'text-succession-success'
      : activity.iconColor;

  return (
    <div
      className={`${cardClasses} rounded-xl border h-full w-full flex flex-col overflow-hidden transition-all duration-200 ${locked ? '' : 'hover:shadow-succession-md'} relative`}
      role="button"
      tabIndex={locked ? -1 : 0}
      aria-disabled={locked || undefined}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      style={{ minWidth: 0 }}
    >
      {/* Locked ribbon */}
      {locked && (
        <div
          className="absolute -top-px -left-px text-white font-bold uppercase tracking-wider rounded-tl-xl rounded-br-md shadow-sm z-10"
          style={{ backgroundColor: 'var(--succession-warning, #d97706)', fontSize: '11px', lineHeight: '1.2', padding: '2px 8px' }}
        >
          {lockedLabel}
        </div>
      )}
      {/* "Start Here" / "Next Step" corner-ribbon badge */}
      {badgeLabel && !completed && !locked && (
        <div
          className="absolute -top-px -left-px text-white font-bold uppercase tracking-wider rounded-tl-xl rounded-br-md shadow-sm z-10"
          style={{ backgroundColor: 'var(--succession-primary, #5489B6)', fontSize: '12px', lineHeight: '1.2', padding: '2px 8px' }}
        >
          {badgeLabel}
        </div>
      )}
      {/* Completion / lock indicator */}
      <div className="absolute top-2 right-2 z-10">
        {locked ? (
          <LockClosedIcon className="h-5 w-5 text-succession-text-muted" aria-hidden="true" />
        ) : completed ? (
          <CheckCircleSolidIcon className="h-5 w-5 text-succession-success" aria-hidden="true" />
        ) : (
          <div className="h-5 w-5 rounded-full border-2 border-succession-border" />
        )}
      </div>
      {/* Compact horizontal content */}
      <div className="px-3 py-2 flex items-center gap-3 flex-1 min-w-0">
        <div className={`shrink-0 rounded-md ${iconContainerClasses} p-1.5`}>
          <IconComponent className={`h-5 w-5 ${iconClasses}`} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-m font-semibold text-succession-text-primary truncate">{activity.title}</div>
          <div className="gs-desc !text-[11px] sm:!text-[12px] md:!text-sm leading-snug text-succession-text-secondary line-clamp-1">
            {activity.shortDescription || activity.description}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStartedMiniCard;


